type Callback = (data: MessageEvent) => void

interface SocketInfo {
	socket: WebSocket
	listener: Callback
	connectionAttempt: number
}

const MAX_CONNECTION_ATTEMPTS = 5

const sockets = new Map<string, SocketInfo>()

const generateQueryParams = (params: Record<string, string> | undefined) => {
	const searchParams = new URLSearchParams(params).toString()
	return (searchParams.length > 0 && `?${searchParams}`) || ''
}

const connectToWebSocket = (topicName: string, listener: Callback) => {
	const webSocketUrl = new URL(topicName)
	const wsConnection = new WebSocket(webSocketUrl)
	wsConnection.addEventListener('open', () => onConnectHandler(topicName))
	wsConnection.addEventListener('close', () => onCloseHandler(topicName))
	wsConnection.addEventListener('message', listener)
	return wsConnection
}

const onCloseHandler = (topicName: string) => {
	localLog(`Connection closed to ${topicName}`)
	const socketInfo = sockets.get(topicName)
	if (socketInfo) {
		if (socketInfo.connectionAttempt < MAX_CONNECTION_ATTEMPTS) {
			// Use a connection timeout of 5 seconds times the connection attempt
			// E.g. for the third connection attempt 15 seconds
			const timeout = socketInfo.connectionAttempt * 5000
			localLog(`Attempting to reconnect in ${timeout / 1000} seconds...`)
			setTimeout(() => {
				socketInfo.connectionAttempt++
				socketInfo.socket = connectToWebSocket(topicName, socketInfo.listener)
			}, timeout)
		} else {
			localLog(`Too many connection attempts to ${topicName}`)
		}
	}
}

const onConnectHandler = (topicName: string) => {
	localLog(`Connected to ${topicName}`)
	const socketInfo = sockets.get(topicName)
	if (socketInfo) {
		socketInfo.connectionAttempt = 0
	}
}

export const subscribeToSocket = (pathName: string, callback: Callback, params?: Record<string, string>) => {
	const topicName = pathName + generateQueryParams(params)
	const socketInfo = sockets.get(topicName)
	if (socketInfo) {
		socketInfo.socket.removeEventListener('message', socketInfo.listener)
		socketInfo.listener = callback
		socketInfo.socket.addEventListener('message', callback)
		localLog('Setting the callback to socket')
	} else {
		try {
			const ws = connectToWebSocket(topicName, callback)
			sockets.set(topicName, {
				socket: ws,
				listener: callback,
				connectionAttempt: 0,
			})
			localLog(`Starting a new socket to ${topicName}`)
		} catch (error) {
			console.error('Error starting websocket: ', error)
		}
	}
}

export const unsubscribeFromSocket = (pathName: string, params?: Record<string, string>) => {
	const topicName = pathName + generateQueryParams(params)
	const socketInfo = sockets.get(topicName)
	if (!socketInfo) {
		localLog('No socket was found to close')
		return
	}
	socketInfo.socket.removeEventListener('message', socketInfo.listener)
	localLog(`Removing callback from socket ${topicName}`)
	socketInfo.socket.close()
	sockets.delete(topicName)
	localLog(`No listeners remaining, closing socket ${topicName}`)
}

/**
 * Closes all sockets. Intended use is to do this after a successful device registration, so that the actual sockets
 * gets an up to date list of all devices. After this has been called, the onClose handler will try to reconnect the
 * socket automatically.
 */
export const resetAllSockets = () => {
	localLog('closing all sockets')
	sockets.forEach((socketInfo) => {
		socketInfo.socket.close()
	})
}

/**
 * This is here for local debugging, not necessary to always have the message, but
 * bothersome to add and remove them, so toggle it here.
 */
const localLog = (_message: string) => {
	if (import.meta.env.DEV) {
		//console.log(_message)
	}
}
